import React, { useState, useEffect, useRef } from 'react';
import { personalProjectsAPI } from '../../services/api';
import ApiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './SimpleProjectPopup.css';

// Global submission lock to prevent any duplicate calls across all instances
let globalSubmissionLock = false;

const SimpleProjectPopup = ({ isOpen, onClose, onSuccess, project = null, isEdit = false }) => {
  const { updateCachedProject } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_type: 'personal',
    technologies_used: [],
    links: [],
    screenshots: [],
    is_public: true, // Default to public
    experience_id: null, // Associate with experience
  });

  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [projectId, setProjectId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0);
  
  // Technology selection states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Frontend');
  const [customTech, setCustomTech] = useState('');

  // User experiences for association
  const [userExperiences, setUserExperiences] = useState([]);
  const [experiencesLoading, setExperiencesLoading] = useState(false);

  // Debug userExperiences changes
  useEffect(() => {
    console.log('userExperiences state changed:', userExperiences);
  }, [userExperiences]);

  // Technology categories
  const technologyCategories = {
    'Frontend': ['React', 'Vue.js', 'Angular', 'Next.js', 'Nuxt.js', 'Svelte', 'HTML', 'CSS', 'JavaScript', 'TypeScript', 'Tailwind CSS', 'Bootstrap', 'SASS', 'SCSS'],
    'Backend': ['Node.js', 'Express.js', 'Python', 'Django', 'Flask', 'FastAPI', 'PHP', 'Laravel', 'Java', 'Spring Boot', 'C#', '.NET', 'Ruby', 'Rails', 'Go', 'Rust'],
    'Database': ['MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'SQLite', 'Firebase', 'Supabase', 'Prisma', 'Sequelize', 'Mongoose'],
    'Cloud & DevOps': ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Jenkins', 'GitHub Actions', 'Vercel', 'Netlify', 'Heroku'],
    'Mobile': ['React Native', 'Flutter', 'Ionic', 'Xamarin', 'Swift', 'Kotlin', 'Android Studio', 'Xcode'],
    'Tools': ['Git', 'GitHub', 'GitLab', 'VS Code', 'Webpack', 'Vite', 'Figma', 'Adobe XD', 'Sketch', 'Postman', 'Insomnia']
  };

  // Fetch user experiences for association
  const fetchUserExperiences = async () => {
    setExperiencesLoading(true);
    try {
      console.log('Fetching user experiences...');
      const response = await ApiService.get('/experiences');
      console.log('Experiences response:', response);
      console.log('Response success:', response.success);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Response data length:', response.data?.length);
      
      if (response.success) {
        const experiences = response.data || [];
        console.log('Setting user experiences:', experiences);
        setUserExperiences(experiences);
        console.log('User experiences set successfully');
      } else {
        console.log('Response was not successful:', response);
      }
    } catch (error) {
      console.error('Error fetching user experiences:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setExperiencesLoading(false);
    }
  };

  // Technology helper functions
  const toggleTechnology = (tech) => {
    const currentTechs = formData.technologies_used || [];
    if (currentTechs.includes(tech)) {
      setFormData(prev => ({
        ...prev,
        technologies_used: currentTechs.filter(t => t !== tech)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        technologies_used: [...currentTechs, tech]
      }));
    }
  };

  const removeTechnology = (tech) => {
    setFormData(prev => ({
      ...prev,
      technologies_used: (prev.technologies_used || []).filter(t => t !== tech)
    }));
  };

  const addCustomTechnology = () => {
    if (customTech.trim() && !formData.technologies_used?.includes(customTech.trim())) {
      setFormData(prev => ({
        ...prev,
        technologies_used: [...(prev.technologies_used || []), customTech.trim()]
      }));
      setCustomTech('');
    }
  };

  const getFilteredTechnologies = () => {
    const categoryTechs = technologyCategories[selectedCategory] || [];
    return categoryTechs.filter(tech => 
      tech.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Links helper functions
  const addLink = () => {
    const newLink = {
      id: Date.now(), // temporary ID
      link_type: 'github',
      link_url: '',
      link_title: ''
    };
    setFormData(prev => ({
      ...prev,
      links: [...(prev.links || []), newLink]
    }));
  };

  const updateLink = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const removeLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  // Screenshot helper functions
  const handleScreenshotUpload = async (event, targetIndex = null) => {
    const files = Array.from(event.target.files);
    const maxFiles = 5;
    const currentCount = formData.screenshots.length;
    
    if (files.length === 0) return;
    
    const file = files[0]; // Only take the first file for individual upload
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!file.type || !allowedTypes.includes(file.type)) {
      const errorMsg = 'Invalid file type. Please upload an image (JPEG, PNG, GIF, or WebP).';
      if (targetIndex !== null) {
        setErrors(prev => ({
          ...prev,
          [`screenshot_${targetIndex}`]: errorMsg
        }));
        setUploadStatus(prev => ({
          ...prev,
          [targetIndex]: 'error'
        }));
      } else {
        setErrors({ general: errorMsg });
      }
      event.target.value = '';
      return;
    }
    
    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const errorMsg = `File size too large (${fileSizeMB}MB). Maximum size is 5MB.`;
      if (targetIndex !== null) {
        setErrors(prev => ({
          ...prev,
          [`screenshot_${targetIndex}`]: errorMsg
        }));
        setUploadStatus(prev => ({
          ...prev,
          [targetIndex]: 'error'
        }));
      } else {
        setErrors({ general: errorMsg });
      }
      event.target.value = '';
      return;
    }
    
    // Create preview URL immediately
    const previewUrl = URL.createObjectURL(file);
    const screenshotId = Date.now();
    
    console.log('Created preview URL:', previewUrl, 'for file:', file.name);
    
    // If targetIndex is provided, replace the screenshot at that index
    if (targetIndex !== null) {
      // Clear any previous errors for this index
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`screenshot_${targetIndex}`];
        return newErrors;
      });

      // Clear upload status (will be set to 'uploading' in uploadScreenshotToAPI)
      setUploadStatus(prev => ({
        ...prev,
        [targetIndex]: null
      }));

      const newScreenshot = {
        id: screenshotId,
        file: file,
        url: previewUrl,
        isFeature: formData.screenshots[targetIndex]?.isFeature || false, // Keep existing feature status
        name: file.name,
        uploaded: false,
        uploading: false
      };

      console.log('Replacing screenshot at index:', targetIndex, 'with:', newScreenshot);

      setFormData(prev => {
        const newScreenshots = [...prev.screenshots];
        newScreenshots[targetIndex] = newScreenshot;
        console.log('Updated screenshots array:', newScreenshots);
        return {
          ...prev,
          screenshots: newScreenshots
        };
      });

      // Upload the image
      await uploadScreenshotToAPI(newScreenshot, targetIndex);
    } else {
      // Add new screenshot to the first empty slot
      if (currentCount >= maxFiles) {
        setErrors({ 
          general: `Maximum ${maxFiles} screenshots allowed.` 
        });
        return;
      }

      const newScreenshot = {
        id: screenshotId,
        file: file,
        url: previewUrl,
        isFeature: currentCount === 0, // First image is feature by default
        name: file.name,
        uploaded: false,
        uploading: false
      };

      console.log('Adding new screenshot:', newScreenshot);

      setFormData(prev => {
        const newScreenshots = [...prev.screenshots, newScreenshot];
        console.log('Added new screenshot, updated array:', newScreenshots);
        return {
          ...prev,
          screenshots: newScreenshots
        };
      });

      // Upload the image
      await uploadScreenshotToAPI(newScreenshot, currentCount);
    }
    
    // Clear the input
    event.target.value = '';
  };

  // Upload screenshot to API
  const uploadScreenshotToAPI = async (screenshot, index) => {
    if (!projectId) {
      console.log('No project ID available yet, will upload after project is created');
      // Mark as pending upload
      setUploadStatus(prev => ({
        ...prev,
        [index]: 'pending'
      }));
      return;
    }

    console.log('Starting upload for screenshot:', screenshot.name, 'to project:', projectId);

    // Set uploading status
    setUploadStatus(prev => ({
      ...prev,
      [index]: 'uploading'
    }));

    setUploadProgress(prev => ({
      ...prev,
      [index]: 0
    }));

    try {
      // Create FormData for file upload
      const uploadFormData = new FormData();
      uploadFormData.append('screenshot', screenshot.file); // Backend expects 'screenshot' field
      uploadFormData.append('project_id', projectId);
      uploadFormData.append('type', 'screenshot');
      uploadFormData.append('is_feature', screenshot?.isFeature || false);
      
      console.log('FormData created:', {
        screenshot: screenshot?.file?.name,
        project_id: projectId,
        type: 'screenshot',
        is_feature: screenshot?.isFeature || false
      });
      
      // Upload file with progress tracking
      const response = await personalProjectsAPI.uploadFile(uploadFormData, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({
          ...prev,
          [index]: progress
        }));
      });

      console.log('Upload response:', response);

      if (response && response.success) {
        // Update screenshot with server response
        setFormData(prev => {
          const newScreenshots = [...prev.screenshots];
          newScreenshots[index] = {
            ...newScreenshots[index],
            uploaded: true,
            uploading: false,
            serverUrl: response.url,
            serverId: response.filename || response.path // Use filename or path as ID
          };
          return {
            ...prev,
            screenshots: newScreenshots
          };
        });

        // Ensure success status is set
        setUploadStatus(prev => {
          const newStatus = {
            ...prev,
            [index]: 'success'
          };
          console.log('Setting upload status to success for index:', index, 'New status:', newStatus);
          return newStatus;
        });

        console.log('Screenshot uploaded successfully:', response);

        // Keep success badge visible - no need to hide it
      } else {
        console.error('Upload failed - unexpected response:', response);
        
        // Extract error message from response
        let errorMessage = 'Failed to upload image. Please try again.';
        
        // Check for validation errors in response
        if (response?.errors) {
          const validationErrors = response.errors;
          
          // Priority order for checking error fields
          const errorFields = ['screenshot', 'file', 'image', 'screenshot.*', 'file.*', 'image.*'];
          
          // Try to find error in priority fields first
          let foundError = null;
          for (const field of errorFields) {
            if (validationErrors[field]) {
              foundError = validationErrors[field];
              break;
            }
          }
          
          // If not found in priority fields, get the first available error
          if (!foundError && Object.keys(validationErrors).length > 0) {
            const firstKey = Object.keys(validationErrors)[0];
            foundError = validationErrors[firstKey];
          }
          
          // Extract the error message
          if (foundError) {
            errorMessage = Array.isArray(foundError) ? foundError[0] : foundError;
          }
        } else if (response?.message) {
          errorMessage = response.message;
        } else if (response?.error) {
          errorMessage = response.error;
        }
        
        setUploadStatus(prev => ({
          ...prev,
          [index]: 'error'
        }));
        
        setErrors(prev => ({
          ...prev,
          [`screenshot_${index}`]: errorMessage
        }));
      }
    } catch (error) {
      console.error('Error uploading screenshot:', error);
      console.error('Error response:', error.response);
      
      setUploadStatus(prev => ({
        ...prev,
        [index]: 'error'
      }));

      // Extract proper error message from different error structures
      let errorMessage = 'Failed to upload image. Please try again.';
      
      // Handle 422 validation errors from Laravel
      if (error.response?.status === 422) {
        const responseData = error.response?.data;
        
        // Check for Laravel validation errors structure
        if (responseData?.errors) {
          const validationErrors = responseData.errors;
          
          // Priority order for checking error fields
          const errorFields = ['screenshot', 'file', 'image', 'screenshot.*', 'file.*', 'image.*'];
          
          // Try to find error in priority fields first
          let foundError = null;
          for (const field of errorFields) {
            if (validationErrors[field]) {
              foundError = validationErrors[field];
              break;
            }
          }
          
          // If not found in priority fields, get the first available error
          if (!foundError && Object.keys(validationErrors).length > 0) {
            const firstKey = Object.keys(validationErrors)[0];
            foundError = validationErrors[firstKey];
          }
          
          // Extract the error message
          if (foundError) {
            errorMessage = Array.isArray(foundError) ? foundError[0] : foundError;
          } else if (responseData?.message) {
            // Fallback to message if errors object is empty
            errorMessage = responseData.message;
          }
        } else if (responseData?.message) {
          // Direct validation message
          errorMessage = responseData.message;
        }
      } else if (error.response?.data?.message) {
        // Direct error message from backend (non-422)
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        // Alternative error field
        errorMessage = error.response.data.error;
      } else if (error.message) {
        // Network or other error messages
        if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Upload timeout. The file may be too large. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }

      setErrors(prev => ({
        ...prev,
        [`screenshot_${index}`]: errorMessage
      }));
    }
  };

  // Retry uploading a failed screenshot
  const retryScreenshotUpload = async (index) => {
    const screenshot = formData.screenshots[index];
    if (!screenshot || !screenshot.file) {
      console.error('Cannot retry: screenshot or file not found');
      return;
    }

    // Clear previous error
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`screenshot_${index}`];
      return newErrors;
    });

    // Reset upload status and retry
    setUploadStatus(prev => ({
      ...prev,
      [index]: 'uploading'
    }));

    // Retry the upload
    await uploadScreenshotToAPI(screenshot, index);
  };

  const removeScreenshot = (index) => {
    // Clear error state for this index
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`screenshot_${index}`];
      return newErrors;
    });

    // Clear upload status for this index
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[index];
      return newStatus;
    });

    // Clear upload progress for this index
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[index];
      return newProgress;
    });

    setFormData(prev => {
      // Clean up the object URL to prevent memory leaks
      if (prev.screenshots[index]?.url) {
        URL.revokeObjectURL(prev.screenshots[index].url);
      }
      
      const newScreenshots = prev.screenshots.filter((_, i) => i !== index);
      // If we removed the feature image, make the first remaining image the feature
      if (newScreenshots.length > 0 && !newScreenshots.some(s => s && s.isFeature)) {
        // Find the first valid screenshot (not undefined/null)
        const firstValidScreenshot = newScreenshots.find(s => s != null);
        if (firstValidScreenshot) {
          firstValidScreenshot.isFeature = true;
        }
      }
      
      // Update selected index
      if (newScreenshots.length === 0) {
        setSelectedScreenshotIndex(0);
      } else if (index === selectedScreenshotIndex) {
        // If we removed the selected item, select the previous one or first one
        setSelectedScreenshotIndex(Math.max(0, index - 1));
      } else if (index < selectedScreenshotIndex) {
        // If we removed an item before the selected one, adjust the index
        setSelectedScreenshotIndex(selectedScreenshotIndex - 1);
      }
      
      return {
        ...prev,
        screenshots: newScreenshots
      };
    });
  };

  const setFeatureImage = (index) => {
    setFormData(prev => ({
      ...prev,
      screenshots: prev.screenshots.map((screenshot, i) => {
        // Only update if screenshot exists
        if (!screenshot) return screenshot;
        return {
          ...screenshot,
          isFeature: i === index
        };
      })
    }));
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
    e.dataTransfer.effectAllowed = 'move';
    
    // Add dragging class to the dragged element
    e.target.closest('.list-item').classList.add('dragging');
    
    // Add dragging class to body for global cursor
    document.body.classList.add('dragging');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Add drop zone indicator
    const listItem = e.target.closest('.list-item');
    if (listItem) {
      listItem.classList.add('drag-over');
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    // Clean up drag states
    document.body.classList.remove('dragging');
    document.querySelectorAll('.list-item').forEach(item => {
      item.classList.remove('dragging', 'drag-over');
    });
    
    if (dragIndex === dropIndex) return;

    setFormData(prev => {
      const newScreenshots = [...prev.screenshots];
      const draggedItem = newScreenshots[dragIndex];
      
      // Remove dragged item
      newScreenshots.splice(dragIndex, 1);
      
      // Insert at new position
      newScreenshots.splice(dropIndex, 0, draggedItem);
      
      // Update selected index if needed
      let newSelectedIndex = selectedScreenshotIndex;
      if (selectedScreenshotIndex === dragIndex) {
        newSelectedIndex = dropIndex;
      } else if (dragIndex < selectedScreenshotIndex && dropIndex >= selectedScreenshotIndex) {
        newSelectedIndex = selectedScreenshotIndex - 1;
      } else if (dragIndex > selectedScreenshotIndex && dropIndex <= selectedScreenshotIndex) {
        newSelectedIndex = selectedScreenshotIndex + 1;
      }
      
      setSelectedScreenshotIndex(newSelectedIndex);
      
      return {
        ...prev,
        screenshots: newScreenshots
      };
    });
  };

  const handleDragEnd = (e) => {
    // Clean up drag states
    document.body.classList.remove('dragging');
    document.querySelectorAll('.list-item').forEach(item => {
      item.classList.remove('dragging', 'drag-over');
    });
  };

  // Computed values
  const selectedScreenshot = formData.screenshots[selectedScreenshotIndex] || null;
  const isSubmittingRef = useRef(false);
  const lastSubmissionTimeRef = useRef(0);
  const submissionIdRef = useRef(0);
  const buttonClickCountRef = useRef(0);

  // Initialize form data when editing
  useEffect(() => {
    if (isEdit && project) {
      console.log('=== EDIT MODE INITIALIZATION ===');
      console.log('Project data received:', project);
      console.log('isEdit flag:', isEdit);
      // Map the project data to the expected format
      const technologies = project.technologies ? 
        project.technologies.map(tech => tech.technology_name || tech) : 
        (project.technologies_used || []);
      
      const links = project.links ? 
        project.links.map(link => ({
          link_type: link.link_type,
          link_url: link.link_url,
          link_title: link.link_title
        })) : 
        (project.links || []);
      
      const screenshots = project.screenshots ? 
        project.screenshots.map(screenshot => ({
          url: screenshot.file_url,
          name: screenshot.file_name,
          isFeature: screenshot.is_feature || false,
          uploaded: true,
          serverUrl: screenshot.file_url,
          serverId: screenshot.id
        })) : 
        (project.screenshots || []);

      const mappedFormData = {
        title: project.title || '',
        description: project.description || '',
        project_type: project.project_type || 'personal',
        technologies_used: technologies,
        links: links,
        screenshots: screenshots,
        is_public: project.is_public !== undefined ? project.is_public : true,
        experience_id: project.experience_id || null,
      };
      
      console.log('Mapped form data:', mappedFormData);
      console.log('Technologies mapped:', technologies);
      console.log('Links mapped:', links);
      console.log('Screenshots mapped:', screenshots);
      
      setFormData(mappedFormData);
      setProjectId(project.id);
      
      // Set upload status for existing screenshots
      if (screenshots.length > 0) {
        const initialUploadStatus = {};
        screenshots.forEach((_, index) => {
          initialUploadStatus[index] = 'success';
        });
        setUploadStatus(initialUploadStatus);
      }
      
      // Always start at Step 1 for edit mode to allow full navigation
      console.log('Setting starting step to 1 for edit mode');
      setCurrentStep(1);
    } else if (!isEdit) {
      // Reset to step 1 for new projects
      setCurrentStep(1);
      setProjectId(null);
    }
  }, [isEdit, project]);

  // Reset form when popup is closed
  useEffect(() => {
    if (!isOpen) {
      // Reset all form data
      setFormData({
        title: '',
        description: '',
        project_type: 'personal',
        technologies_used: [],
        links: [],
        screenshots: [],
        is_public: true,
        experience_id: null,
      });
      setCurrentStep(1);
      setProjectId(null);
      setUploadStatus({});
      setUploadProgress({});
      setErrors({});
      setSuccessMessage('');
      setSelectedScreenshotIndex(0);
    }
  }, [isOpen]);

  // Auto-upload pending screenshots when projectId becomes available
  useEffect(() => {
    if (projectId && formData.screenshots.length > 0) {
      const hasPendingUploads = formData.screenshots.some((screenshot, index) => {
        const status = uploadStatus[index];
        return status === 'pending' && screenshot.file;
      });
      
      if (hasPendingUploads) {
        uploadPendingScreenshots();
      }
    }
  }, [projectId]);

  // Debug upload status changes
  useEffect(() => {
    console.log('Upload status changed:', uploadStatus);
  }, [uploadStatus]);

  // Reset submission state when popup opens/closes
  useEffect(() => {
    if (isOpen) {
      isSubmittingRef.current = false;
      setIsLoading(false);
      lastSubmissionTimeRef.current = 0;
      submissionIdRef.current = 0;
      buttonClickCountRef.current = 0;
      globalSubmissionLock = false;
      setSuccessMessage(''); // Reset success message when popup opens
      setErrors({}); // Reset errors when popup opens
      setSelectedScreenshotIndex(0); // Reset selected screenshot
      // Fetch user experiences when modal opens
      fetchUserExperiences();
    } else {
      // Clean up object URLs when popup closes
      formData.screenshots.forEach(screenshot => {
        if (screenshot.url && screenshot.url.startsWith('blob:')) {
          URL.revokeObjectURL(screenshot.url);
        }
      });
    }
  }, [isOpen]);

  // Set first screenshot as selected when screenshots are added
  useEffect(() => {
    if (formData.screenshots.length > 0 && selectedScreenshotIndex >= formData.screenshots.length) {
      setSelectedScreenshotIndex(0);
    }
  }, [formData.screenshots.length, selectedScreenshotIndex]);

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Clean up all object URLs to prevent memory leaks
      formData.screenshots.forEach(screenshot => {
        if (screenshot.url && screenshot.url.startsWith('blob:')) {
          URL.revokeObjectURL(screenshot.url);
        }
      });
    };
  }, []);

  // Debug formData changes
  useEffect(() => {
    console.log('FormData screenshots changed:', formData.screenshots);
  }, [formData.screenshots]);

  // Upload pending screenshots when projectId is available
  useEffect(() => {
    if (projectId && formData.screenshots.length > 0) {
      // Check for pending uploads
      const pendingScreenshots = formData.screenshots.filter((screenshot, index) => 
        screenshot.file && uploadStatus[index] === 'pending'
      );
      
      if (pendingScreenshots.length > 0) {
        console.log('Found pending screenshots, starting uploads...');
        pendingScreenshots.forEach((screenshot, pendingIndex) => {
          const actualIndex = formData.screenshots.findIndex(s => s.id === screenshot.id);
          if (actualIndex !== -1) {
            uploadScreenshotToAPI(screenshot, actualIndex);
          }
        });
      }
    }
  }, [projectId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleNextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleSkipStep = () => {
    // Check if user is trying to skip without completing step 1 (only for new projects)
    if (currentStep > 1 && !projectId && !isEdit) {
      setErrors({ 
        general: 'Please complete step 1 first to create the project before skipping other steps.' 
      });
      return;
    }
    
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleSaveTechnologies = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      // Use the project_id from step 1
      if (!projectId) {
        setErrors({ 
          general: 'Project ID not found. Please go back to step 1 and create the project first.' 
        });
        return;
      }

      // Send technologies data with step information
      const technologiesData = {
        step: 'technologies',
        technologies_used: formData.technologies_used || []
      };

      // Save technologies data to the project
      const response = await personalProjectsAPI.update(projectId, technologiesData);

      if (response && response.data) {
        // Move to next step first
        setCurrentStep(3);
        
        // Show success message on the new step
        setTimeout(() => {
          setSuccessMessage(`✅ Technologies saved! Now let's work on Links.`);
        }, 100);
        
        // Clear success message after some time
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving technologies:', error);
      setErrors({ 
        general: 'Failed to save technologies. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLinks = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      // Use the project_id from step 1
      if (!projectId) {
        setErrors({ 
          general: 'Project ID not found. Please go back to step 1 and create the project first.' 
        });
        return;
      }

      // Send links data with step information
      const linksData = {
        step: 'links',
        links: formData.links || []
      };

      // Save links data to the project
      const response = await personalProjectsAPI.update(projectId, linksData);

      if (response && response.data) {
        // Move to next step first
        setCurrentStep(4);
        
        // Show success message on the new step
        setTimeout(() => {
          setSuccessMessage(`✅ Links saved! Now let's work on Screenshots.`);
        }, 100);
        
        // Clear success message after some time
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving links:', error);
      setErrors({ 
        general: 'Failed to save links. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to upload pending screenshots
  const uploadPendingScreenshots = async () => {
    const pendingScreenshots = formData.screenshots.filter((screenshot, index) => {
      const status = uploadStatus[index];
      return status === 'pending' && screenshot.file;
    });

    for (let i = 0; i < pendingScreenshots.length; i++) {
      const screenshot = pendingScreenshots[i];
      const index = formData.screenshots.findIndex(s => s === screenshot);
      await uploadScreenshotToAPI(screenshot, index);
    }
  };

  const handleSaveScreenshots = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      // Use the project_id from step 1
      if (!projectId) {
        setErrors({ 
          general: 'Project ID not found. Please go back to step 1 and create the project first.' 
        });
        return;
      }

      // Upload any pending screenshots first
      await uploadPendingScreenshots();

      // Check if all screenshots are uploaded
      const pendingUploads = formData.screenshots.filter((screenshot, index) => {
        const status = uploadStatus[index];
        return status === 'uploading' || status === 'pending';
      });

      if (pendingUploads.length > 0) {
        setErrors({ 
          general: `Please wait for ${pendingUploads.length} image(s) to finish uploading.` 
        });
        return;
      }

      // Filter out screenshots that failed to upload
      const uploadedScreenshots = formData.screenshots.filter((screenshot, index) => {
        const status = uploadStatus[index];
        return status === 'success' || screenshot.uploaded;
      });

      console.log('Uploaded screenshots:', uploadedScreenshots);
      console.log('Upload status:', uploadStatus);
      console.log('Form data screenshots:', formData.screenshots);

      let response;
      
      // If no screenshots were uploaded successfully, but we have screenshots in formData,
      // it means they were uploaded but the status wasn't updated correctly
      if (uploadedScreenshots.length === 0 && formData.screenshots.length > 0) {
        console.log('No screenshots marked as uploaded, but screenshots exist in formData. Using all screenshots.');
        // Use all screenshots from formData as they were likely uploaded successfully
        const screenshotsData = {
          step: 'screenshots',
          screenshots: formData.screenshots.filter(s => s != null).map(screenshot => ({
            url: screenshot.serverUrl || screenshot.url,
            is_feature: screenshot?.isFeature || false,
            name: screenshot?.name || screenshot?.file?.name,
            id: screenshot?.serverId
          }))
        };
        
        console.log('Screenshots data to send:', screenshotsData);
        response = await personalProjectsAPI.update(projectId, screenshotsData);
        console.log('API response:', response);
      } else if (uploadedScreenshots.length > 0) {
        // Prepare screenshots data for the API
        const screenshotsData = {
          step: 'screenshots',
          screenshots: uploadedScreenshots.filter(s => s != null).map(screenshot => ({
            url: screenshot.serverUrl || screenshot.url,
            is_feature: screenshot?.isFeature || false,
            name: screenshot?.name || screenshot?.file?.name,
            id: screenshot?.serverId
          }))
        };

        console.log('Screenshots data to send:', screenshotsData);
        // Save screenshots data to the project
        response = await personalProjectsAPI.update(projectId, screenshotsData);
        console.log('API response:', response);
      } else {
        // No screenshots to save, but still mark the step as completed
        console.log('No screenshots to save, marking step as completed');
        const screenshotsData = {
          step: 'screenshots',
          screenshots: []
        };
        
        console.log('Screenshots data to send:', screenshotsData);
        response = await personalProjectsAPI.update(projectId, screenshotsData);
        console.log('API response:', response);
      }

      console.log('Final response check:', response);
      console.log('Response success:', response?.success);
      console.log('Response data:', response?.data);
      
      if (response && response.success) {
        console.log('Screenshots saved successfully, moving to step 5');
        // Move to next step first
        setCurrentStep(5);
        
        // Show success message on the new step
        setTimeout(() => {
          setSuccessMessage(`✅ Screenshots saved! Now let's publish your project.`);
        }, 100);
        
        // Clear success message after some time
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        console.error('Unexpected response structure:', response);
        setErrors({ 
          general: 'Failed to save screenshots. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error saving screenshots:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      // Check if it's a network error or API error
      if (error.response) {
        // API responded with error status
        setErrors({ 
          general: error.response.data?.message || 'Failed to save screenshots. Please try again.' 
        });
      } else if (error.request) {
        // Network error
        setErrors({ 
          general: 'Network error. Please check your connection and try again.' 
        });
      } else {
        // Other error
        setErrors({ 
          general: 'Failed to save screenshots. Please try again.' 
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = async () => {
    if (!projectId) {
      setErrors({ 
        general: 'Project ID not found. Please go back to step 1 and create the project first.' 
      });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Publish the project with the visibility setting
      const publishData = {
        step: 'publish',
        is_public: formData.is_public,
        status: 'published'
      };

      console.log('Publishing project with data:', publishData);
      const response = await personalProjectsAPI.update(projectId, publishData);
      console.log('Publish response:', response);

      if (response && response.success) {
        console.log('Project published successfully');
        // Pass the complete project data for proper state update
        const publishedProject = response.data || { id: projectId, status: 'published', is_public: formData.is_public };
        onSuccess(publishedProject);
        onClose();
      } else {
        console.error('Unexpected publish response:', response);
        setErrors({ 
          general: 'Failed to publish project. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error publishing project:', error);
      setErrors({ 
        general: 'Failed to publish project. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Generate unique submission ID
    const currentSubmissionId = ++submissionIdRef.current;
    const now = Date.now();
    
    // Prevent double submission using multiple checks including global lock
    if (globalSubmissionLock || isSubmittingRef.current || isLoading || (now - lastSubmissionTimeRef.current < 3000)) {
      return;
    }
    
    // Set all locks
    globalSubmissionLock = true;
    isSubmittingRef.current = true;
    lastSubmissionTimeRef.current = now;
    setIsLoading(true);
    setErrors({});

    try {
      const submitData = { ...formData };
      
      // Clean up experience_id - convert empty string to null
      if (submitData.experience_id === '') {
        submitData.experience_id = null;
      }
      
      console.log('Submitting form data:', submitData);
      console.log('Experience ID being sent:', submitData.experience_id);

      let response;
      if (isEdit && project) {
        console.log('Updating existing project...');
        // Add step information for updates
        submitData.step = 'basic_info';
        response = await personalProjectsAPI.update(project.id, submitData);
      } else {
        console.log('Creating new project...');
        response = await personalProjectsAPI.create(submitData);
      }

      // If we reach here without error, the API call was successful
      // Check if response exists and has data
      if (response && response.data) {
        const createdProject = response.data.data || response.data;
        setProjectId(createdProject.id);
        
        if (isEdit) {
          // If editing, move to next step instead of closing
          setErrors({}); // Clear any previous errors
          
          // Move to next step and show success message there
          const stepNames = {
            1: 'Project Information',
            2: 'Technologies',
            3: 'Links',
            4: 'Screenshots',
            5: 'Publish'
          };
          
          const currentStepName = stepNames[currentStep];
          const nextStepName = stepNames[currentStep + 1];
          
          // Move to next step first
          setCurrentStep(2);
          
          // Show success message on the new step
          setTimeout(() => {
            setSuccessMessage(`✅ ${currentStepName} updated successfully! Now let's work on ${nextStepName}.`);
          }, 100);
          
          // Clear success message after some time
          setTimeout(() => {
            setSuccessMessage('');
          }, 3000);
        } else {
          // If creating new project, upload any pending screenshots first
          await uploadPendingScreenshots();
          
          // Move to next step and show success message there
          const stepNames = {
            1: 'Project Information',
            2: 'Technologies',
            3: 'Links',
            4: 'Screenshots',
            5: 'Publish'
          };
          
          const currentStepName = stepNames[currentStep];
          const nextStepName = stepNames[currentStep + 1];
          
          // Move to next step first
          setCurrentStep(2);
          setErrors({}); // Clear any previous errors
          
          // Show success message on the new step
          setTimeout(() => {
            setSuccessMessage(`✅ ${currentStepName} completed successfully! Now let's work on ${nextStepName}.`);
          }, 100);
          
          // Clear success message after some time
          setTimeout(() => {
            setSuccessMessage('');
          }, 3000);
        }
      }
      } catch (error) {
        console.error('Error saving project:', error);
        
        // Handle different error structures
        if (error.response?.status === 422 && error.response?.data?.errors) {
          // Laravel validation errors
          setErrors(error.response.data.errors);
        } else if (error.response?.data?.message) {
          // Direct error message
          setErrors({ 
            general: error.response.data.message 
          });
        } else if (error.message) {
          // Direct error message
          setErrors({ 
            general: error.message 
          });
        } else {
          // Network or other errors
          setErrors({ 
            general: 'Failed to save project. Please check your connection and try again.' 
          });
        }
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
      globalSubmissionLock = false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="simple-popup-overlay">
      {/* Prevent clicks during submission */}
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.1)',
            cursor: 'not-allowed'
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      )}
      <div className="simple-popup">
        <div className="simple-popup-header">
          <div className="simple-popup-title">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                <polyline points="14,2 14,8 20,8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10,9 9,9 8,9"></polyline>
              </svg>
            </div>
            <div className="header-content">
              <h3>{isEdit ? 'Edit Project' : 'Add Your Project'}</h3>
              <p>{isEdit ? 'Update your project details' : 'Showcase your creativity and skills'}</p>
            </div>
          </div>
          <button className="simple-popup-close" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="step-indicator">
            <div className="step-indicator-content">
              <div className={`step-item ${currentStep >= 1 ? 'active' : ''} ${isEdit ? 'editable' : ''}`}>
                <div className="step-circle">1</div>
                <div className="step-label">Basic Info</div>
              </div>
              <div className="step-line"></div>
              <div className={`step-item ${currentStep >= 2 ? 'active' : ''} ${isEdit ? 'editable' : ''}`}>
                <div className="step-circle">2</div>
                <div className="step-label">Technologies</div>
              </div>
              <div className="step-line"></div>
              <div className={`step-item ${currentStep >= 3 ? 'active' : ''} ${isEdit ? 'editable' : ''}`}>
                <div className="step-circle">3</div>
                <div className="step-label">Links</div>
              </div>
              <div className="step-line"></div>
              <div className={`step-item ${currentStep >= 4 ? 'active' : ''} ${isEdit ? 'editable' : ''}`}>
                <div className="step-circle">4</div>
                <div className="step-label">Screenshots</div>
              </div>
              <div className="step-line"></div>
              <div className={`step-item ${currentStep >= 5 ? 'active' : ''} ${isEdit ? 'editable' : ''}`}>
                <div className="step-circle">5</div>
                <div className="step-label">Publish</div>
              </div>
             </div>
         </div>

        <div className="simple-popup-content">
          
          
          {errors.general && (
            <div className="simple-popup-error">
              <i className="bi bi-exclamation-triangle"></i>
              {errors.general}
            </div>
          )}


          {/* Success Message - positioned above form content */}
          {successMessage && (
            <div className="simple-popup-success success-above-buttons">
              <i className="bi bi-check-circle"></i>
              {successMessage}
            </div>
          )}


          {/* Step 1: Project Information */}
          {currentStep === 1 && (
            <div className="form-section">
            <div className="section-header">
              <div className="section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10,9 9,9 8,9"></polyline>
                </svg>
              </div>
              <div className="section-title">
                <h4>Project Information</h4>
                <p>Tell us about your project and its purpose</p>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">
                  Project Title *
                  <span className="field-hint">A clear, descriptive name for your project</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={errors.title ? 'error' : ''}
                  placeholder="e.g., E-Commerce Web Application, AI Chatbot"
                  required
                />
                {errors.title && <span className="error-text">{errors.title[0]}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="project_type">
                  Project Type *
                  <span className="field-hint">What kind of project is this?</span>
                </label>
                <select
                  id="project_type"
                  name="project_type"
                  value={formData.project_type}
                  onChange={handleInputChange}
                  className={errors.project_type ? 'error' : ''}
                  required
                >
                  <option value="personal">Personal Project</option>
                  <option value="academic">Academic Project</option>
                  <option value="final_year">Final Year Project</option>
                  <option value="hackathon">Hackathon Project</option>
                  <option value="open_source">Open Source Contribution</option>
                </select>
                {errors.project_type && <span className="error-text">{errors.project_type[0]}</span>}
              </div>
            </div>

            {/* Experience Association Field */}
            <div className="form-group">
              <label htmlFor="experience_id">
                Associate with Experience (Optional)
                <span className="field-hint">Link this project to a professional experience</span>
              </label>
              <select
                id="experience_id"
                name="experience_id"
                value={formData.experience_id || ''}
                onChange={handleInputChange}
                className={errors.experience_id ? 'error' : ''}
                disabled={experiencesLoading}
              >
                <option value="">No association</option>
                {(() => {
                  console.log('Rendering dropdown - userExperiences:', userExperiences);
                  console.log('userExperiences.length:', userExperiences.length);
                  console.log('experiencesLoading:', experiencesLoading);
                  
                  if (userExperiences.length > 0) {
                    return userExperiences.map(experience => {
                      console.log('Rendering experience option:', experience);
                      return (
                        <option key={experience.id} value={experience.id}>
                          {experience.title} at {experience.company}
                        </option>
                      );
                    });
                  } else if (!experiencesLoading) {
                    return <option value="" disabled>No experiences available</option>;
                  }
                  return null;
                })()}
              </select>
              {experiencesLoading && <span className="field-hint">Loading experiences...</span>}
              {!experiencesLoading && userExperiences.length === 0 && (
                <span className="field-hint">Add professional experiences first to associate projects</span>
              )}
              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <span className="field-hint" style={{fontSize: '10px', color: '#999'}}>
                  Debug: {userExperiences.length} experiences loaded
                </span>
              )}
              {errors.experience_id && <span className="error-text">{errors.experience_id[0]}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">
                Project Description *
                <span className="field-hint">Describe what your project does and its main features</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className={errors.description ? 'error' : ''}
                placeholder="Describe your project, its purpose, key features, and what problem it solves..."
                rows="4"
                required
              />
              {errors.description && <span className="error-text">{errors.description[0]}</span>}
            </div>
          </div>
          )}

          {/* Step 2: Technologies */}
          {currentStep === 2 && (
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                    <polyline points="2,17 12,22 22,17"></polyline>
                    <polyline points="2,12 12,17 22,12"></polyline>
                  </svg>
                </div>
                <div className="section-title">
                  <h4>Technologies Used</h4>
                  <p>Select the technologies, frameworks, and tools you used in this project.</p>
                </div>
              </div>
              
              {/* Technology Selection Interface */}
              <div className="technology-selection">
                {/* Search and Filter */}
                <div className="tech-search-section">
                  <div className="search-input">
                    <i className="bi bi-search"></i>
                    <input
                      type="text"
                      placeholder="Search technologies..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Selected Technologies */}
                {formData.technologies_used && formData.technologies_used.length > 0 && (
                  <div className="selected-technologies">
                    <h5>Selected Technologies ({formData.technologies_used.length})</h5>
                    <div className="selected-tech-tags">
                      {formData.technologies_used.map((tech, index) => (
                        <div key={index} className="tech-tag selected">
                          <span>{tech}</span>
                          <button
                            type="button"
                            onClick={() => removeTechnology(tech)}
                            className="remove-tech"
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Technology Categories */}
                <div className="tech-categories">
                  <h5>Popular Technologies</h5>
                  <div className="category-tabs">
                    {Object.keys(technologyCategories).map(category => (
                      <button
                        key={category}
                        type="button"
                        className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Technology Grid */}
                <div className="technology-grid">
                  {getFilteredTechnologies().map(tech => (
                    <button
                      key={tech}
                      type="button"
                      className={`tech-option ${formData.technologies_used?.includes(tech) ? 'selected' : ''}`}
                      onClick={() => toggleTechnology(tech)}
                      disabled={formData.technologies_used?.includes(tech)}
                    >
                      <span className="tech-name">{tech}</span>
                      {formData.technologies_used?.includes(tech) && (
                        <i className="bi bi-check-circle-fill selected-icon"></i>
                      )}
                    </button>
                  ))}
                </div>

                {/* Custom Technology Input */}
                <div className="custom-tech-section">
                  <div className="custom-tech-input">
                    <input
                      type="text"
                      placeholder="Add custom technology..."
                      value={customTech}
                      onChange={(e) => setCustomTech(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addCustomTechnology()}
                    />
                    <button
                      type="button"
                      onClick={addCustomTechnology}
                      className="add-custom-btn"
                      disabled={!customTech.trim()}
                    >
                      <i className="bi bi-plus"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Links */}
          {currentStep === 3 && (
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                </div>
                <div className="section-title">
                  <h4>Project Links</h4>
                  <p>Share links to your project (optional)</p>
                </div>
              </div>
              
              {/* Links Management */}
              <div className="links-management">
                {/* Add Link Button */}
                <div className="add-link-section">
                  <button
                    type="button"
                    onClick={addLink}
                    className="add-link-btn"
                  >
                    <i className="bi bi-plus-lg"></i>
                    Add Link
                  </button>
                </div>

                {/* Links List */}
                {formData.links && formData.links.length > 0 && (
                  <div className="links-list">
                    {formData.links.map((link, index) => (
                      <div key={link.id || index} className="link-item">
                        <div className="link-fields">
                          <div className="link-type-field">
                            <label>Type</label>
                            <select
                              value={link.link_type}
                              onChange={(e) => updateLink(index, 'link_type', e.target.value)}
                            >
                              <option value="github">GitHub</option>
                              <option value="live_demo">Live Demo</option>
                              <option value="demo">Demo</option>
                              <option value="documentation">Documentation</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          
                          <div className="link-url-field">
                            <label>URL</label>
                            <input
                              type="url"
                              value={link.link_url}
                              onChange={(e) => updateLink(index, 'link_url', e.target.value)}
                              placeholder="https://example.com"
                            />
                          </div>
                          
                          <div className="link-title-field">
                            <label>Title (Optional)</label>
                            <input
                              type="text"
                              value={link.link_title}
                              onChange={(e) => updateLink(index, 'link_title', e.target.value)}
                              placeholder="Link description"
                            />
                          </div>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => removeLink(index)}
                          className="remove-link-btn"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty State */}
                {(!formData.links || formData.links.length === 0) && (
                  <div className="empty-links-state">
                    <i className="bi bi-link-45deg"></i>
                    <p>No links added yet. Click "Add Link" to get started.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Screenshots */}
          {currentStep === 4 && (
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21,15 16,10 5,21"></polyline>
                  </svg>
                </div>
                <div className="section-title">
                  <h4>Project Screenshots</h4>
                  <p>Add visual content to showcase your project (optional)</p>
                </div>
              </div>
              
              {/* Fiverr-style Screenshot Upload Interface */}
              <div className="fiverr-screenshots-upload">
                <div className="screenshots-grid">
                  {/* Create 5 individual upload boxes */}
                  {Array.from({ length: 5 }, (_, index) => {
                    const screenshot = formData.screenshots[index];
                    const isEmpty = !screenshot;
                    
                    console.log(`Box ${index}:`, { 
                      screenshot, 
                      isEmpty, 
                      url: screenshot?.url,
                      totalScreenshots: formData.screenshots.length,
                      allScreenshots: formData.screenshots
                    });
                    
                    return (
                      <div key={index} className={`screenshot-box ${isEmpty ? 'empty' : 'filled'}`}>
                        {isEmpty ? (
                          // Empty upload box
                          <div className="upload-box">
                            <input
                              type="file"
                              id={`screenshot-${index}`}
                              name={`screenshot-${index}`}
                              accept="image/*"
                              onChange={(e) => handleScreenshotUpload(e, index)}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor={`screenshot-${index}`} className="upload-label">
                              <div className="upload-content">
                                <div className="upload-icon">
                                  <i className="bi bi-cloud-upload"></i>
                                </div>
                                <div className="upload-text">
                                  <h6>Add Screenshot</h6>
                                  <p>Click to upload</p>
                                </div>
                              </div>
                            </label>
                          </div>
                        ) : (
                          // Filled screenshot box with image preview
                          <div className="screenshot-content">
                            <div className="screenshot-preview">
                              <img 
                                src={screenshot.url} 
                                alt={screenshot.name}
                                onLoad={() => console.log('Image loaded successfully:', screenshot.name)}
                                onError={(e) => {
                                  console.error('Image failed to load:', screenshot.url, e);
                                  e.target.style.display = 'none';
                                }}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                              />
                              
                              {/* Upload Pending Overlay */}
                              {uploadStatus[index] === 'pending' && (
                                <div className="upload-pending-overlay">
                                  <div className="pending-content">
                                    <div className="pending-icon">
                                      <i className="bi bi-clock"></i>
                                    </div>
                                    <div className="pending-text">Waiting to upload...</div>
                                  </div>
                                </div>
                              )}

                              {/* Upload Progress Overlay */}
                              {uploadStatus[index] === 'uploading' && (
                                <div className="upload-progress-overlay">
                                  <div className="progress-content">
                                    <div className="progress-icon">
                                      <i className="bi bi-cloud-upload"></i>
                                    </div>
                                    <div className="progress-text">Uploading...</div>
                                    <div className="progress-bar">
                                      <div 
                                        className="progress-fill" 
                                        style={{ width: `${uploadProgress[index] || 0}%` }}
                                      ></div>
                                    </div>
                                    <div className="progress-percentage">{uploadProgress[index] || 0}%</div>
                                  </div>
                                </div>
                              )}

                              {/* Upload Success Badge */}
                              {uploadStatus[index] === 'success' && (
                                <div className="upload-success-badge">
                                  <i className="bi bi-check-circle-fill"></i>
                                </div>
                              )}

                              {/* Upload Error Overlay */}
                              {uploadStatus[index] === 'error' && (
                                <div className="upload-error-overlay">
                                  <div className="error-content">
                                    <i className="bi bi-exclamation-triangle-fill"></i>
                                    <span>Upload Failed</span>
                                    <div className="error-actions">
                                      <label
                                        htmlFor={`retry-screenshot-${index}`}
                                        className="retry-upload-btn"
                                        title="Select image to upload again"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <i className="bi bi-arrow-clockwise"></i>
                                        <span>Retry</span>
                                      </label>
                                      <button
                                        type="button"
                                        className="remove-upload-btn"
                                        title="Remove this image"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeScreenshot(index);
                                        }}
                                      >
                                        <i className="bi bi-trash"></i>
                                        <span>Remove</span>
                                      </button>
                                    </div>
                                    <input
                                      type="file"
                                      id={`retry-screenshot-${index}`}
                                      name={`retry-screenshot-${index}`}
                                      accept="image/*"
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleScreenshotUpload(e, index);
                                      }}
                                      style={{ display: 'none' }}
                                    />
                                  </div>
                                </div>
                              )}

                              {screenshot?.isFeature && (
                                <div className="feature-badge">
                                  <i className="bi bi-star-fill"></i>
                                  <span>Featured</span>
                                </div>
                              )}
                              
                              {/* Overlay with actions - only show when not uploading or pending */}
                              {uploadStatus[index] !== 'uploading' && uploadStatus[index] !== 'pending' && (
                                <div className="screenshot-overlay">
                                  <div className="overlay-actions">
                                    <button
                                      type="button"
                                      onClick={() => setFeatureImage(index)}
                                      className={`overlay-btn feature-btn ${screenshot?.isFeature ? 'active' : ''}`}
                                      title={screenshot?.isFeature ? 'Currently featured' : 'Set as feature image'}
                                    >
                                      <i className={`bi ${screenshot?.isFeature ? 'bi-star-fill' : 'bi-star'}`}></i>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeScreenshot(index)}
                                      className="overlay-btn remove-btn"
                                      title="Remove this image"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                    <label htmlFor={`replace-screenshot-${index}`} className="overlay-btn replace-btn" title="Replace image">
                                      <i className="bi bi-arrow-clockwise"></i>
                                    </label>
                                    <input
                                      type="file"
                                      id={`replace-screenshot-${index}`}
                                      name={`replace-screenshot-${index}`}
                                      accept="image/*"
                                      onChange={(e) => handleScreenshotUpload(e, index)}
                                      style={{ display: 'none' }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="screenshot-info">
                              <div className="screenshot-name" title={screenshot.name}>
                                {screenshot.name.length > 15 
                                  ? screenshot.name.substring(0, 15) + '...' 
                                  : screenshot.name
                                }
                              </div>
                              {uploadStatus[index] === 'error' && (
                                <div className="upload-error-text">
                                  {errors[`screenshot_${index}`] || 'Upload failed'}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Instructions */}
                <div className="upload-instructions">
                  <div className="instruction-item">
                    <i className="bi bi-star"></i>
                    <span>Click the star to set as featured image</span>
                  </div>
                  <div className="instruction-item">
                    <i className="bi bi-arrow-clockwise"></i>
                    <span>Click the refresh icon to replace an image</span>
                  </div>
                  <div className="instruction-item">
                    <i className="bi bi-trash"></i>
                    <span>Click the trash icon to remove an image</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Publish */}
          {currentStep === 5 && (
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12l2 2 4-4"></path>
                    <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
                    <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
                    <path d="M13 12h3a2 2 0 0 1 2 2v1"></path>
                    <path d="M8 12H5a2 2 0 0 0-2 2v1"></path>
                  </svg>
                </div>
                <div className="section-title">
                  <h4>Publish Project</h4>
                  <p>Review and publish your project</p>
                </div>
              </div>
              
              {/* Project Summary */}
              <div className="project-summary">
                <h5>Project Summary:</h5>
                <div className="summary-item">
                  <strong>Title:</strong> {formData.title}
                </div>
                <div className="summary-item">
                  <strong>Type:</strong> {formData.project_type?.charAt(0).toUpperCase() + formData.project_type?.slice(1)} Project
                </div>
                <div className="summary-item">
                  <strong>Technologies:</strong> {formData.technologies_used?.length || 0} technologies added
                </div>
                <div className="summary-item">
                  <strong>Links:</strong> {formData.links?.length || 0} links added
                </div>
                <div className="summary-item">
                  <strong>Screenshots:</strong> {formData.screenshots?.length || 0} images uploaded
                </div>
              </div>

              {/* Visibility Settings */}
              <div className="visibility-settings">
                <h5>Visibility Settings:</h5>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_public"
                      checked={formData.is_public || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked }))}
                      style={{
                        width: '18px',
                        height: '18px',
                        marginRight: '12px',
                        borderRadius: '4px',
                        backgroundColor: formData.is_public ? '#8b5cf6' : 'white'
                      }}
                    />
                    <span className="checkbox-text">Make this project public</span>
                  </label>
                  <p className="field-hint">
                    Public projects will be visible to other users and can be discovered in search results.
                  </p>
                </div>
              </div>

              {/* Publishing Tips */}
              <div className="publishing-tips">
                <h5>Publishing Tips:</h5>
                <ul>
                  <li>Double-check all your information for accuracy</li>
                  <li>Make sure all links are working and accessible</li>
                  <li>Ensure your screenshots showcase your project well</li>
                  <li>Your project will be visible to potential employers and collaborators</li>
                </ul>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="form-actions">
            <div className="form-actions-left">
              {currentStep > 1 && (
                <button 
                  type="button" 
                  onClick={handlePrevStep} 
                  className="btn btn-secondary"
                >
                  <i className="bi bi-arrow-left"></i>
                  Previous
                </button>
              )}
              {currentStep === 1 && (
                <button 
                  type="button" 
                  onClick={onClose} 
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              )}
            </div>
            
            <div className="form-actions-right">
              {currentStep > 1 && currentStep < 5 && (
                <button 
                  type="button" 
                  onClick={handleSkipStep} 
                  className="btn btn-outline"
                >
                  Skip
                </button>
              )}
              
              {currentStep === 1 ? (
                <button 
                  type="button" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // Prevent if already processing
                    if (isLoading || isSubmittingRef.current) {
                      return;
                    }
                    
                    handleSubmit(e);
                  }}
                  className="btn btn-primary" 
                  disabled={isLoading || isSubmittingRef.current}
                >
                  {isLoading ? (
                    <>
                      <i className="bi bi-arrow-clockwise animate-spin"></i>
                      {isEdit ? 'Updating Project...' : 'Creating Project...'}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg"></i>
                      Save & Continue
                    </>
                  )}
                </button>
              ) : currentStep === 2 ? (
                <button 
                  type="button" 
                  onClick={handleSaveTechnologies}
                  className="btn btn-primary" 
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="bi bi-arrow-clockwise animate-spin"></i>
                      <span className="btn-text-desktop">Saving Technologies...</span>
                      <span className="btn-text-mobile">Saving...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg"></i>
                      <span className="btn-text-desktop">Save Technologies</span>
                      <span className="btn-text-mobile">Next</span>
                    </>
                  )}
                </button>
              ) : currentStep === 3 ? (
                <button 
                  type="button" 
                  onClick={handleSaveLinks}
                  className="btn btn-primary" 
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="bi bi-arrow-clockwise animate-spin"></i>
                      <span className="btn-text-desktop">Saving Links...</span>
                      <span className="btn-text-mobile">Saving...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg"></i>
                      <span className="btn-text-desktop">Save Links</span>
                      <span className="btn-text-mobile">Next</span>
                    </>
                  )}
                </button>
              ) : currentStep === 4 ? (
                <button 
                  type="button" 
                  onClick={handleSaveScreenshots}
                  className="btn btn-primary" 
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="bi bi-arrow-clockwise animate-spin"></i>
                      <span className="btn-text-desktop">Saving Screenshots...</span>
                      <span className="btn-text-mobile">Saving...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg"></i>
                      <span className="btn-text-desktop">Save Screenshots</span>
                      <span className="btn-text-mobile">Next</span>
                    </>
                  )}
                </button>
              ) : currentStep === 5 ? (
                <button 
                  type="button" 
                  onClick={handleFinish} 
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="bi bi-arrow-clockwise animate-spin"></i>
                      <span className="btn-text-desktop">Publishing...</span>
                      <span className="btn-text-mobile">Publishing...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg"></i>
                      <span className="btn-text-desktop">Publish Project</span>
                      <span className="btn-text-mobile">Publish</span>
                    </>
                  )}
                </button>
              ) : (
                <button 
                  type="button" 
                  onClick={handleNextStep} 
                  className="btn btn-primary"
                >
                  Next
                  <i className="bi bi-arrow-right"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleProjectPopup;
